import { ref, computed, nextTick } from 'vue';

export function useIsotopeFiltering(getCategorySymbols = null) {
  const BASELINE_OPACITY = '0.3';
  const ACTIVE_OPACITY = '1';
  const BASE_TILE_SELECTOR = ':scope > path:not([data-animated-clone]), :scope > rect:not([data-animated-clone])';

  // Filter categories - these will correspond to different colors/types
  const filterCategories = ref([
    {
      id: 'current_portfolio',
      label: 'Current Isotope Offering (click the isotopes to learn more)',
      color: '#96FDEB', // Light blue - matches current active isotopes
      active: false
    },
    {
      id: 'rd_precursor',
      label: 'Isotopes in R&D',
      color: '#F5CEFF', // Pink/purple
      active: false
    },
    {
      id: 'other_tech',
      label: 'Other Isotopes and Opportunities - we are happy to discuss isotopes beyond our current portfolio',
      color: '#2E4AA4', // Blue
      active: false
    }
  ]);

  // Track which filters are currently active
  const activeFilters = computed(() => 
    filterCategories.value.filter(category => category.active)
  );

  // Check if any filters are active
  const hasActiveFilters = computed(() => activeFilters.value.length > 0);

  const normalizeSymbol = (value) => {
    if (typeof value !== 'string') return null;
    const normalized = value.trim().toLowerCase();
    return normalized || null;
  };

  const toSymbolSet = (values) => {
    if (!Array.isArray(values)) return new Set();

    return new Set(
      values
        .map((value) => {
          if (typeof value === 'string') return normalizeSymbol(value);
          if (value && typeof value === 'object') return normalizeSymbol(value.symbol);
          return null;
        })
        .filter(Boolean)
    );
  };

  const getCategorySymbolSets = () => {
    if (typeof getCategorySymbols !== 'function') {
      return {};
    }

    const categorySymbols = getCategorySymbols() || {};
    return Object.fromEntries(
      Object.entries(categorySymbols).map(([categoryId, values]) => [
        categoryId,
        toSymbolSet(values)
      ])
    );
  };

  const getSymbolCategoryId = (symbol, categorySymbolSets) => {
    const normalizedSymbol = normalizeSymbol(symbol);
    if (!normalizedSymbol) return null;

    if (categorySymbolSets.current_portfolio?.has(normalizedSymbol)) return 'current_portfolio';
    if (categorySymbolSets.rd_precursor?.has(normalizedSymbol)) return 'rd_precursor';
    if (categorySymbolSets.other_tech?.has(normalizedSymbol)) return 'other_tech';
    return null;
  };

  const getCategoryStyle = (categoryId) => {
    if (categoryId === 'current_portfolio') {
      return { fill: '#96FDEB', text: '#000000' };
    }
    if (categoryId === 'rd_precursor') {
      return { fill: '#F5CEFF', text: '#000000' };
    }
    if (categoryId === 'other_tech') {
      return { fill: '#2E4AA4', text: '#FFFFFF' };
    }
    if (categoryId === 'uncategorized') {
      return { fill: '#CBD1FF', text: '#1E2442' };
    }
    return null;
  };

  const applySVGCategoryStyle = (group, categoryId) => {
    const style = getCategoryStyle(categoryId);
    if (!style) return;

    const firstShape = group.querySelector(BASE_TILE_SELECTOR);
    if (!firstShape) return;

    firstShape.setAttribute('fill', style.fill);

    const otherPaths = group.querySelectorAll('path');
    const otherRects = group.querySelectorAll('rect');
    [...otherPaths, ...otherRects].forEach((element) => {
      if (element === firstShape || element.getAttribute('data-animated-clone') === 'true') return;
      element.setAttribute('fill', style.text);
    });

    const textElements = group.querySelectorAll('text, tspan');
    textElements.forEach((element) => {
      element.setAttribute('fill', style.text);
    });
  };

  // Toggle a filter on/off
  const toggleFilter = (filterId) => {
    const filter = filterCategories.value.find(f => f.id === filterId);
    if (filter) {
      filter.active = !filter.active;
      applyFiltersToSVG();
    }
  };

  // Apply filters to both SVG elements and mobile grid
  const applyFiltersToSVG = () => {
    nextTick(() => {
      // Handle SVG elements
      const svg = document.getElementById('periodic_table');
      if (svg) {
        const allGroups = Array.from(svg.querySelectorAll('g[id]')).filter(
          (group) => Boolean(group.querySelector(BASE_TILE_SELECTOR))
        );
        const categorySymbolSets = getCategorySymbolSets();
        const hasCategorySymbols = Object.keys(categorySymbolSets).length > 0;
        const activeFilterIds = activeFilters.value.map(filter => filter.id);
        
        allGroups.forEach(group => {
          const symbol = group.getAttribute('id');

          if (hasCategorySymbols) {
            const categoryId = getSymbolCategoryId(symbol, categorySymbolSets) || 'uncategorized';
            applySVGCategoryStyle(group, categoryId);

            const shouldShow = hasActiveFilters.value
              ? activeFilterIds.includes(categoryId)
              : categoryId !== 'uncategorized';

            group.style.opacity = shouldShow ? ACTIVE_OPACITY : BASELINE_OPACITY;
            group.style.transition = 'opacity 0.3s ease';
            return;
          }

          if (!hasActiveFilters.value) {
            group.style.opacity = ACTIVE_OPACITY;
            return;
          }

          const firstShape = group.querySelector(BASE_TILE_SELECTOR);
          if (!firstShape) return;
          const activeColors = activeFilters.value.map(filter => filter.color.toLowerCase());
          const currentFill = firstShape.getAttribute('fill');
          const isMatchingFilter = activeColors.includes(currentFill?.toLowerCase());

          group.style.opacity = isMatchingFilter ? ACTIVE_OPACITY : BASELINE_OPACITY;
          group.style.transition = 'opacity 0.3s ease';
        });
      }

      // Handle mobile grid elements
      const mobileGrid = document.querySelector('.isotope-mobile-grid');
      if (mobileGrid) {
        const allIsotopes = mobileGrid.querySelectorAll('.isotope_single');
        const categorySymbolSets = getCategorySymbolSets();
        const hasCategorySymbols = Object.keys(categorySymbolSets).length > 0;
        const activeFilterIds = activeFilters.value.map(filter => filter.id);
        
        allIsotopes.forEach(isotope => {
          const symbol = isotope.getAttribute('id');

          if (hasCategorySymbols) {
            const categoryId = getSymbolCategoryId(symbol, categorySymbolSets) || 'uncategorized';
            const shouldShow = hasActiveFilters.value
              ? activeFilterIds.includes(categoryId)
              : categoryId !== 'uncategorized';

            isotope.style.opacity = shouldShow ? ACTIVE_OPACITY : BASELINE_OPACITY;
            isotope.style.transition = 'opacity 0.3s ease';
            return;
          }

          if (!hasActiveFilters.value) {
            isotope.style.opacity = ACTIVE_OPACITY;
            return;
          }

          const activeColors = activeFilters.value.map(filter => filter.color.toLowerCase());
          const bgColor = isotope.style.backgroundColor;
          const hexColor = rgbToHex(bgColor);
          const isMatchingFilter = hexColor ? activeColors.includes(hexColor.toLowerCase()) : false;

          isotope.style.opacity = isMatchingFilter ? ACTIVE_OPACITY : BASELINE_OPACITY;
          isotope.style.transition = 'opacity 0.3s ease';
        });
      }
    });
  };

  const rgbToHex = (rgb) => {
    if (!rgb || typeof rgb !== 'string') return null;
    
    // Handle hex colors that are already in the right format
    if (rgb.startsWith('#')) return rgb;
    
    // Handle rgb() format
    const result = rgb.match(/\d+/g);
    if (!result || result.length < 3) return null;
    
    const r = parseInt(result[0], 10);
    const g = parseInt(result[1], 10);
    const b = parseInt(result[2], 10);
    
    // Validate RGB values
    if (isNaN(r) || isNaN(g) || isNaN(b)) return null;
    if (r < 0 || r > 255 || g < 0 || g > 255 || b < 0 || b > 255) return null;
    
    return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
  };

  // Filter isotopes for modal display
  const filterIsotopesForModal = (isotopes) => {
    if (!hasActiveFilters.value) {
      return isotopes;
    }
    return isotopes.filter(isotope => {
      return true;
    });
  };

  // Get the color for a specific isotope based on its category
  const getIsotopeColor = (isotope) => {
    if (isotope.active) {
      return '#96FDEB'; // Current portfolio
    }
    return '#7F73E6'; // Default inactive
  };

  // Reset all filters
  const clearAllFilters = () => {
    filterCategories.value.forEach(filter => {
      filter.active = false;
    });
    applyFiltersToSVG();
  };

  // Initialize filters (call this when component mounts)
  const initializeFilters = () => {
    applyFiltersToSVG();
  };

  return {
    filterCategories,
    activeFilters,
    hasActiveFilters,
    toggleFilter,
    applyFiltersToSVG,
    filterIsotopesForModal,
    getIsotopeColor,
    clearAllFilters,
    initializeFilters
  };
}
